import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SectionHeading } from '@app/components/core';
import { useProductAuditReport } from '@data/hooks/products';
import type { AuditReportKind, ProductAuditReport } from '@data/utils/types';
import { Notice } from './QualityTab';

/** Links open in a new tab: inside the frame they would replace the report with the page they point at. */
const HEAD_TAG = /<head(?=[\s>])[^>]*>/i;
const withLinksOutward = (html: string): string =>
  HEAD_TAG.test(html)
    ? html.replace(HEAD_TAG, match => `${match}<base target="_blank">`)
    : `<base target="_blank">${html}`;

/**
 * The committed page, as its audit rendered it. Same-origin so its height can be read, and with no
 * `allow-scripts`, so nothing in it runs: a report is a document, and the pair together is what a
 * sandbox forbids.
 */
const ReportFrame = ({ html, title }: { html: string; title: string }): JSX.Element => {
  const frame = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(600);
  const observer = useRef<ResizeObserver | null>(null);

  const fit = useCallback(() => {
    const body = frame.current?.contentDocument?.body;
    if (!body) return;
    const style = frame.current?.contentWindow?.getComputedStyle(body);
    const margins = style ? parseFloat(style.marginTop) + parseFloat(style.marginBottom) : 0;
    // Plus the frame's own 1px border top and bottom, which the height includes.
    setHeight(Math.ceil(body.scrollHeight + margins) + 2);
  }, []);

  // Refitted as the report reflows, since a narrower window makes it taller and a web font arrives late.
  const onLoad = (): void => {
    fit();
    observer.current?.disconnect();
    const body = frame.current?.contentDocument?.body;
    if (body && typeof ResizeObserver !== 'undefined') {
      observer.current = new ResizeObserver(fit);
      observer.current.observe(body);
    }
  };
  useEffect(() => () => observer.current?.disconnect(), []);

  return (
    <iframe
      ref={frame}
      title={title}
      srcDoc={withLinksOutward(html)}
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      onLoad={onLoad}
      className="w-full rounded-2xl border border-border bg-white"
      style={{ height }}
    />
  );
};

/**
 * A productivity or security report, committed to the product's repository as the page its audit
 * renders. Unlike the quality audit, only the page exists, so it is shown as it was rendered rather than
 * rebuilt in Phill's own layout.
 */
const AuditReportTab = ({
  productId,
  kind,
  entry,
  isListLoading,
  isUnavailable = false,
  header,
}: {
  productId?: string;
  kind: AuditReportKind;
  /** The report's index entry, or none when the repository carries no such report. */
  entry?: ProductAuditReport;
  isListLoading: boolean;
  /** GitHub could not be read, which is not the same answer as "no report". */
  isUnavailable?: boolean;
  /** Stands in for the report's own heading, such as the host's audit picker. */
  header?: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useProductAuditReport(productId, kind, entry?.sha);
  const title = t(`auditReports.${kind}.section`);

  let body = <Notice>{t('auditReports.loading')}</Notice>;
  if (!isListLoading && !entry)
    body = <Notice>{isUnavailable ? t('auditReports.unavailable') : t(`auditReports.${kind}.empty`)}</Notice>;
  else if (isError) body = <Notice>{t('auditReports.failed')}</Notice>;
  else if (!isLoading && data?.html) body = <ReportFrame html={data.html} title={title} />;

  return (
    <section className="flex flex-col gap-4">
      {header ?? <SectionHeading>{title}</SectionHeading>}
      {body}
    </section>
  );
};

export default AuditReportTab;
